import React, { useState } from 'react';
import { db, handleFirestoreError, OperationType } from '../firebase';
import { collection, writeBatch, doc, getDocs } from 'firebase/firestore';
import { Upload, FileUp, Check, AlertCircle, Loader2, Database, Info, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import Papa from 'papaparse';
import { useAcademicYear } from '../context/AcademicYearContext';
import { dataCache } from '../services/dataCacheService';

const ImportCSV: React.FC = () => {
  const { selectedYear, currentYearObj } = useAcademicYear();
  const [preview, setPreview] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        console.log("Raw CSV results:", results);
        
        if (results.data.length === 0) {
          setMessage({ text: 'הקובץ ריק או שאינו בפורמט נכון', type: 'error' });
          setLoading(false);
          return;
        }

        const mappedData = results.data.map((row: any) => {
          // Robust header detection
          const findVal = (keys: string[]) => {
            const foundKey = Object.keys(row).find(k => 
              keys.some(key => k.trim().includes(key) || key.includes(k.trim()))
            );
            return foundKey ? row[foundKey] : '';
          };

          return {
            fullName: findVal(['שם מלא', 'שם תלמיד', 'fullName', 'שם']),
            nationalId: findVal(['תעודת זהות', 'ת"ז', 'ת.ז', 'nationalId', 'זהות']),
            birthDate: findVal(['תאריך לידה', 'ת"ל', 'ת.ל', 'birthDate']),
            address: findVal(['כתובת', 'address']),
            fatherName: findVal(['שם אבא', 'fatherName']),
            fatherPhone: findVal(['נייד אבא', 'טלפון אבא', 'fatherPhone']),
            motherName: findVal(['שם אמא', 'motherName']),
            motherPhone: findVal(['נייד אמא', 'טלפון אמא', 'motherPhone']),
            className: findVal(['כיתה', 'className']),
            transportName: findVal(['מסיע', 'קו', 'transportName']),
            escortName: findVal(['מלווה', 'escortName']),
            healthNotes: findVal(['הערות בריאותיות', 'הערות', 'healthNotes'])
          };
        }).filter(s => s.fullName && s.nationalId && s.className);
        
        console.log("Mapped data:", mappedData);

        if (mappedData.length === 0) {
          setMessage({ 
            text: 'לא נמצאו נתונים תקינים. וודא שהכותרות נכונות (שם, ת"ז, כיתה) ושהקובץ שמור כ-CSV UTF-8.', 
            type: 'error' 
          });
        } else {
          setPreview(mappedData);
          setMessage({ text: '', type: '' });
        }
        setLoading(false);
      },
      error: (error) => {
        console.error(error);
        setMessage({ text: 'שגיאה בקריאת הקובץ', type: 'error' });
        setLoading(false);
      }
    });
  };

  const handleImport = async () => {
    if (preview.length === 0) return;
    setImporting(true);
    setMessage({ text: '', type: '' });

    try {
      // 1. Fetch all classes to create a name -> id map for efficiency
      const classesSnapshot = await getDocs(collection(db, 'classes'));
      if (classesSnapshot.empty) {
        setMessage({ text: 'לא נמצאו כיתות במערכת. עליך להקים כיתות ב"ניהול כיתות" לפני ייבוא תלמידים.', type: 'error' });
        setImporting(false);
        return;
      }

      const classMap: Record<string, string> = {};
      classesSnapshot.forEach(doc => {
        classMap[doc.data().name.trim()] = doc.id;
      });

      const batch = writeBatch(db);
      let newCount = 0;
      let updatedCount = 0;
      let skipCount = 0;

      // Fetch all students to check for existing students
      const studentsSnapshot = await getDocs(collection(db, 'students'));
      const existingStudentsMap: Record<string, { id: string; enrollments?: any; data: any }> = {};
      studentsSnapshot.docs.forEach(d => {
        const data = d.data();
        if (data.nationalId) {
          existingStudentsMap[data.nationalId] = {
            id: d.id,
            enrollments: data.enrollments || {},
            data
          };
        }
      });

      const processedIdsInThisFile = new Set<string>();

      preview.forEach((row) => {
        const className = row.className?.trim();
        const classId = classMap[className];

        if (!classId) {
          console.warn(`כיתה לא נמצאה: ${className}`);
          skipCount++;
          return;
        }

        if (processedIdsInThisFile.has(row.nationalId)) {
          // Skip internal duplicate within the same CSV file
          return;
        }
        processedIdsInThisFile.add(row.nationalId);

        const existing = existingStudentsMap[row.nationalId];
        const enrollmentData = {
          classId: classId,
          className: className,
          enrolledAt: new Date().toISOString()
        };

        if (existing) {
          // Existing student from repository: update their enrollment for selected year!
          const ref = doc(db, 'students', existing.id);
          batch.update(ref, {
            classId: classId,
            className: className,
            address: row.address || existing.data.address || '',
            fatherPhone: row.fatherPhone || existing.data.fatherPhone || '',
            motherPhone: row.motherPhone || existing.data.motherPhone || '',
            transportName: row.transportName || existing.data.transportName || '',
            escortName: row.escortName || existing.data.escortName || '',
            healthNotes: row.healthNotes || existing.data.healthNotes || '',
            [`enrollments.${selectedYear}`]: enrollmentData
          });
          updatedCount++;
        } else {
          // Brand new student
          const newDocRef = doc(collection(db, 'students'));
          batch.set(newDocRef, {
            ...row,
            classId: classId,
            className: className,
            enrollments: {
              [selectedYear]: enrollmentData
            },
            active: true,
            createdAt: new Date().toISOString()
          });
          newCount++;
        }
      });

      const totalProcessed = newCount + updatedCount;
      if (totalProcessed > 0) {
        await batch.commit();
        dataCache.remove('students');

        let msg = `הייבוא הושלם בהצלחה לשנת ${currentYearObj.name}!`;
        const parts: string[] = [];
        if (newCount > 0) parts.push(`${newCount} תלמידים חדשים שנוספו למאגר`);
        if (updatedCount > 0) parts.push(`${updatedCount} תלמידים קיימים שעודכנו ושובצו מחדש`);
        if (skipCount > 0) parts.push(`${skipCount} שורות דולגו עקב שם כיתה לא קיים`);

        if (parts.length > 0) {
          msg += ` (${parts.join(', ')})`;
        }

        setMessage({ 
          text: msg, 
          type: 'success' 
        });
        setPreview([]);
      } else {
        setMessage({ text: 'לא נמצאו נתונים תקינים לייבוא. וודא ששמות הכיתות בקובץ תואמים בדיוק לכיתות שהוגדרו במערכת.', type: 'error' });
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'students');
      console.error(error);
      setMessage({ text: 'שגיאה בשמירת הנתונים במסד הנתונים', type: 'error' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" dir="rtl">
      {/* Academic Year Notice Banner */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-4 rounded-2xl border border-indigo-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600 text-white rounded-xl">
            <Calendar size={20} />
          </div>
          <div>
            <div className="text-xs text-indigo-700 font-bold">שנת הלימודים הפעילה לקליטת קובץ ושיבוץ</div>
            <div className="text-sm font-black text-slate-800">{currentYearObj.name}</div>
          </div>
        </div>
        <span className="text-xs px-3 py-1 bg-white text-indigo-700 rounded-full font-bold border border-indigo-200 shadow-xs">
          שנה נבחרת
        </span>
      </div>

      <div className="bg-white p-10 rounded-3xl shadow-sm border border-slate-100 text-center">
        <div className="inline-flex p-5 bg-indigo-50 text-indigo-600 rounded-2xl mb-6">
          <Upload size={36} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 mb-3">ייבוא רשימת תלמידים</h2>
        <p className="text-slate-500 mb-8 max-w-md mx-auto leading-relaxed">
          העלה קובץ CSV עם העמודות: <code className="bg-slate-100 px-1 rounded text-indigo-600">שם מלא</code>, <code className="bg-slate-100 px-1 rounded text-indigo-600">ת"ז</code>, <code className="bg-slate-100 px-1 rounded text-indigo-600">כיתה</code>.
          <br />
          <span className="text-xs mt-2 block opacity-75">* וודא ששמות הכיתות בקובץ תואמים בדיוק לשמות הכיתות במערכת.</span>
        </p>

        <label className="relative cursor-pointer group inline-block">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
          <div className="inline-flex items-center gap-3 bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-4 rounded-2xl transition-all shadow-xl shadow-indigo-100 font-bold text-lg">
            <FileUp size={22} />
            בחירת קובץ CSV
          </div>
        </label>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center p-12 gap-3">
          <Loader2 className="animate-spin text-indigo-600" size={40} />
          <p className="text-slate-500 font-medium">מעבד את הקובץ...</p>
        </div>
      )}

      {preview.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
        >
          <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3 text-indigo-600 font-bold">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Database size={20} />
              </div>
              <span>תצוגה מקדימה ({preview.length} תלמידים נמצאו)</span>
            </div>
            <button
              onClick={handleImport}
              disabled={importing}
              className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-2xl transition-all shadow-lg shadow-emerald-100 font-bold disabled:opacity-50 w-full sm:w-auto"
            >
              {importing ? <Loader2 className="animate-spin" size={20} /> : <Check size={20} />}
              אישור וייבוא למערכת
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-100">
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">שם מלא</th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">תעודת זהות</th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">כיתה</th>
                  <th className="px-6 py-4 font-bold text-slate-700 text-sm">מסיע</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {preview.slice(0, 15).map((student, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-slate-900">{student.fullName}</td>
                    <td className="px-6 py-4 font-mono text-slate-500 text-sm">{student.nationalId}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold">
                        {student.className}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">{student.transportName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.length > 15 && (
              <div className="p-5 text-center text-slate-400 text-sm bg-slate-50/30 italic flex items-center justify-center gap-2">
                <Info size={16} />
                מציג 15 שורות ראשונות מתוך {preview.length}...
              </div>
            )}
          </div>
        </motion.div>
      )}

      <AnimatePresence>
        {message.text && (
          <motion.div 
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className={`fixed bottom-8 left-1/2 px-8 py-4 rounded-2xl shadow-2xl text-white font-bold z-50 flex items-center gap-3 ${
              message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
            }`}
          >
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ImportCSV;
